import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Loads multiple translation files and deep-merges them into one object.
 * Each resource defines a prefix and suffix used to build: `{prefix}{lang}{suffix}`.
 *
 * Usage in AppModule:
 *   TranslateModule.forRoot({
 *     loader: {
 *       provide: TranslateLoader,
 *       useFactory: multiTranslateLoaderFactory,
 *       deps: [HttpClient],
 *     },
 *   })
 *
 *   export function multiTranslateLoaderFactory(http: HttpClient) {
 *     return new MultiTranslateHttpLoader(http, [
 *       { prefix: 'assets/i18n/', suffix: '.json' },
 *       { prefix: 'assets/i18n/invoices-v2/', suffix: '.json' },
 *     ]);
 *   }
 */
export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private resources: Array<{ prefix: string; suffix: string }>
  ) {}

  getTranslation(lang: string): Observable<Record<string, unknown>> {
    const requests = this.resources.map(r =>
      this.http
        .get<Record<string, unknown>>(`${r.prefix}${lang}${r.suffix}`)
        .pipe(catchError(() => of({})))
    );

    return forkJoin(requests).pipe(
      map(translations => this.deepMerge({}, ...translations))
    );
  }

  private deepMerge(
    target: Record<string, unknown>,
    ...sources: Record<string, unknown>[]
  ): Record<string, unknown> {
    for (const source of sources) {
      for (const key of Object.keys(source)) {
        const sv = source[key];
        if (sv && typeof sv === 'object' && !Array.isArray(sv)) {
          if (!target[key] || typeof target[key] !== 'object') {
            target[key] = {};
          }
          this.deepMerge(
            target[key] as Record<string, unknown>,
            sv as Record<string, unknown>
          );
        } else {
          target[key] = sv;
        }
      }
    }
    return target;
  }
}
